// Options handling for the chat system
import { useCallback } from 'react';
import { Question } from '@/types/theme';
import { performantLogger } from '@/utils/performantLogger';

export const useOptionsHandler = (
  questions: Question[],
  currentQuestionIndex: number,
  setIsTyping: (isTyping: boolean) => void,
  messages: any[] = []
) => {
  // Function to handle showing options after question is displayed
  const showOptionsAfterQuestion = useCallback((messageId: string, specificQuestionId?: string) => {
    // Generate a unique ID for tracking this function call
    const optionsId = Math.random().toString(36).substring(2, 8);

    // Get the question to display options for
    let questionToUse: Question | undefined;

    // If a specific question ID was provided, find that question
    if (specificQuestionId) {
      questionToUse = questions.find(q => q.id === specificQuestionId);
    } else {
      // Otherwise use the current question index
      const currentIndex = currentQuestionIndex;
      questionToUse = questions[currentIndex];
    }

    if (!questionToUse) {
      performantLogger.chat('OptionsHandler', `[${optionsId}] No question found, cannot show options`);
      return;
    }

    performantLogger.chat('OptionsHandler', `[${optionsId}] Preparing to show options for question`, {
      questionId: questionToUse.id,
      questionType: questionToUse.type,
      hasOptions: !!questionToUse.options?.length,
      messageId,
      timestamp: new Date().toISOString()
    });

    // Set isTyping to false to allow interaction
    setIsTyping(false);
    performantLogger.chat('OptionsHandler', `[${optionsId}] Set isTyping to false`);

    // Wait a short time to ensure the DOM has updated
    setTimeout(() => {
      // Set a custom data attribute on the message element to indicate it should show options
      // This will be used by the MessageList component to know which message should show options
      const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
      if (messageElement) {
        // First check if this message already has a question ID assigned
        const existingQuestionId = messageElement.getAttribute('data-question-id');

        // If it does, and it's different from our current question, find that question instead
        if (existingQuestionId && existingQuestionId !== questionToUse.id) {
          const existingQuestion = questions.find(q => q.id === existingQuestionId);
          if (existingQuestion) {
            // Use the existing question instead
            questionToUse = existingQuestion;
            performantLogger.chat('OptionsHandler', `[${optionsId}] Using existing question ID from message:`, {
              existingQuestionId,
              questionText: existingQuestion.text.substring(0, 30) + '...'
            });
          }
        }

        // Now set the attributes with the correct question
        messageElement.setAttribute('data-show-options', 'true');
        messageElement.setAttribute('data-question-id', questionToUse.id);
        messageElement.setAttribute('data-question-text', questionToUse.text);
        performantLogger.chat('OptionsHandler', `[${optionsId}] Set data attributes on message element`, {
          messageId,
          questionId: questionToUse.id,
          questionText: questionToUse.text.substring(0, 30) + '...'
        });

        // Check if the message element contains the question text
        const messageContent = messageElement.textContent || '';

        // Only update the content if it doesn't match the question text
        // AND if the message doesn't already have content
        if (!messageContent.includes(questionToUse.text) && messageContent.trim() === '') {
          performantLogger.chat('OptionsHandler', `[${optionsId}] Message does not contain question text, updating`, {
            messageId,
            messageContent: messageContent.substring(0, 30) + '...',
            questionText: questionToUse.text.substring(0, 30) + '...'
          });

          // Find the message content element
          const contentElement = messageElement.querySelector('.message-content');
          if (contentElement) {
            // Update the content to include the question text
            contentElement.innerHTML = questionToUse.text;
          }
        } else {
          performantLogger.chat('OptionsHandler', `[${optionsId}] Message already has content, not updating text`);
        }
      } else {
        performantLogger.chat('OptionsHandler', `[${optionsId}] Message element not found for ID: ${messageId}`);
      }

      // Add a custom event to notify the MessageList component that options should be shown
      const optionsEvent = new CustomEvent('showQuestionOptions', {
        detail: {
          messageId,
          questionId: questionToUse.id,
          questionType: questionToUse.type,
          questionText: questionToUse.text,
          timestamp: new Date().toISOString()
        }
      });
      document.dispatchEvent(optionsEvent);
      performantLogger.chat('OptionsHandler', `[${optionsId}] Dispatched showQuestionOptions event`);
    }, 100); // Short delay to ensure DOM is updated
  }, [currentQuestionIndex, questions, setIsTyping, messages]);

  return {
    showOptionsAfterQuestion
  };
};
